import toast from 'react-hot-toast';
import { Toast } from '@components/Toast/ui/Toast';

type ToastType = 'success' | 'error' | 'warning' | 'loading';

interface ShowToastProps {
  type?: ToastType;
  duration?: number;
  title: string;
  text: string;
  /** For an error: the failed request's id, shown as "Ref: ..." (ZLAYT-1519). */
  reference?: string | null;
}

const useToast = () => {
  const showToast = ({
    type = 'success',
    title,
    text,
    duration = 3000,
    reference,
  }: ShowToastProps) => {
    const isLoading = type === 'loading';

    return toast(
      (t) => (
        <Toast
          id={t.id}
          type={type}
          title={title}
          text={text}
          reference={reference}
        />
      ),
      { duration: isLoading ? Infinity : duration }
    );
  };

  const showSuccess = (title: string, text: string, duration: number = 3000) =>
    showToast({ type: 'success', title, text, duration });

  const showError = (title: string, text: string, duration: number = 8000) =>
    showToast({ type: 'error', title, text, duration });

  const showWarning = (title: string, text: string, duration: number = 6000) =>
    showToast({ type: 'warning', title, text, duration });

  const showLoading = (title: string, text: string) =>
    showToast({ type: 'loading', title, text });

  const dismiss = (id?: string) => toast.dismiss(id);

  return {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showLoading,
    dismiss,
  };
};

export default useToast;
